import {
  DetailedHTMLProps, FC, InputHTMLAttributes, PropsWithChildren,
} from 'react';
import { Field, type SupportedInputs } from 'react-final-form';
import classNames from 'classnames';
import type { FieldValidator } from 'final-form';
import getClassNames from 'src/common/utils/getClassNames';
import { withSuffix } from 'src/common/utils/dataAttributeHelpers';
import styles from './Input.module.scss';

 type FontSize = 'body-m' | 'body-l';

type FinalFormFieldProps = {
  validate?: FieldValidator<any>;
  validateFields?: string[];
};

export type TextInputProps =
  Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'ref'> &
  {
    hasError?: boolean;
    disabled?: boolean;
    /**
     * Purpose of parse function is to make sure that value should be removed from
     * values object if it's empty string
     * Reference: https://github.com/final-form/react-final-form/issues/130
     */
    parse?: (value: string) => string;
    'data-test'?: string;
    allowAutoComplete?: boolean;
    fontsize?: FontSize;
    /**
     * Optional React node to display before the input value (e.g., currency symbol).
     */
    leftSlot?: React.ReactNode;
    /**
     * Optional React node to display after the input value (e.g., % symbol).
     */
    rightSlot?: React.ReactNode;
    /** Wrap the input with <Field>. Defaults to true */
    wrapWithField?: boolean;
  } & FinalFormFieldProps;

const FieldWrapper: FC<FinalFormFieldProps &
{
  name: string;
  className?: string;
  wrapWithField?: boolean;
  component?: SupportedInputs;
  parse?: (value: string) => string;
} & PropsWithChildren> = ({
  wrapWithField,
  parse,
  validate,
  validateFields,
  ...args
}) => {
  if (!wrapWithField) {
    const component = args.component;
    // Switch for future changes
    switch (component) {
      case 'input':
      default:
        // Exclude react-final-form specific props when rendering native input
        // by removing them from the args spread
        return <input {...args} />;
    }
  }
  return (
    <Field {...args} parse={parse} validate={validate} validateFields={validateFields} />
  );
};

/**
 * Styled text input for use with react-final-form
 */
const TextInput: FC<TextInputProps> = ({
  name,
  hasError,
  parse = (value) => value,
  'data-test': dataTest,
  allowAutoComplete = true,
  disabled = false,
  fontsize = 'body-l',
  leftSlot,
  rightSlot,
  wrapWithField = true,
  ...props
}) => {
  // Common Field props to avoid duplication
  const fieldProps = {
    type: 'text',
    ...props,
    parse,
    title: props.placeholder,
    component: 'input' as const,
    autoComplete: allowAutoComplete ? 'on' : 'off',
    name: name || '',
    disabled,
    'data-test': withSuffix(dataTest, 'input'),
  };

  // If no leftSlot or rightSlot, render the simple input as before
  if (!leftSlot && !rightSlot) {
    return (
      <FieldWrapper
        wrapWithField={wrapWithField}
        {...fieldProps}
        className={getClassNames(styles, 'input', classNames(fontsize, { hasError, disabled }))}
      />
    );
  }

  // If leftSlot or rightSlot exists, render with wrapper
  return (
    <div className={getClassNames(styles, 'inputWrapper', classNames({ hasError, disabled }))}>
      {leftSlot && (
        <span
          className={getClassNames(styles, 'leftSlot', classNames(fontsize))}
        >
          {leftSlot}
        </span>
      )}
      <FieldWrapper
        wrapWithField={wrapWithField}
        {...fieldProps}
        className={getClassNames(styles, 'input', classNames(fontsize, {
          hasError,
          disabled,
          withLeftSlot: leftSlot,
          withRightSlot: rightSlot,
        }))}
      />
      {rightSlot && (
        <span
          className={getClassNames(styles, 'rightSlot', classNames(fontsize))}
        >
          {rightSlot}
        </span>
      )}
    </div>
  );
};

export default TextInput;
